import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Instagram } from "lucide-react";
import fr from "@/content/fr";

const LOGO_URL =
  "/images/logohd.png";

// TikTok glyph (lucide doesn't ship one)
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.5 6.6a5.7 5.7 0 0 1-3.4-1.1 5.6 5.6 0 0 1-2-2.6h-3v13a2.6 2.6 0 1 1-2.6-2.6c.27 0 .53.04.78.12V10.3a5.7 5.7 0 1 0 4.83 5.62V9a8.7 8.7 0 0 0 5.39 1.85v-3z" />
  </svg>
);

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const links = [
    { id: "hero", label: fr.nav.home, kind: "scroll" },
    { id: "streetwear", label: fr.nav.streetwearTab, kind: "route", to: "/expo-streetwear-sneaker-culture" },
    { id: "beauty", label: fr.nav.beautyTab, kind: "route", to: "/beauty-wellness-weekend" },
    { id: "editions", label: fr.nav.editions, kind: "scroll" },
    { id: "partners", label: fr.nav.partners, kind: "scroll" },
  ];

  const onLinkClick = (l) => {
    if (l.kind === "route") {
      navigate(l.to);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: l.id } });
      return;
    }
    const el = document.getElementById(l.id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="footer"
      className="relative pt-20 pb-10 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, #15042E 0%, #0A0118 100%)",
        }}
      />
      {/* hairline separator */}
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/15 to-transparent mb-14" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <img
              src={LOGO_URL}
              alt="GEM Entertainment"
              className="h-12 w-auto"
              draggable="false"
            />
            <p
              className="mt-6 font-display text-white text-2xl sm:text-3xl leading-tight max-w-[520px] tracking-tight"
              data-testid="footer-tagline"
            >
              « {fr.footer.tagline} »
            </p>
            <a
              href={`mailto:${fr.footer.contact}`}
              data-testid="footer-contact"
              className="mt-6 inline-flex items-center gap-3 text-white/75 hover:text-white text-[15px] transition border-b border-white/15 hover:border-white/50 pb-1"
            >
              {fr.footer.contact}
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="text-[11px] tracking-[0.32em] uppercase text-white/45 font-mono">
              Navigation
            </p>
            <ul className="mt-5 space-y-3">
              {links.map((l) => (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => onLinkClick(l)}
                    className="text-white/70 hover:text-white text-[15px] transition"
                    data-testid={`footer-link-${l.id}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="text-[11px] tracking-[0.32em] uppercase text-white/45 font-mono">
              Social
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                href="https://www.instagram.com/gementertainment.ca"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={fr.footer.socials.instagram}
                data-testid="footer-instagram"
                className="w-11 h-11 rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/45 hover:bg-white/[0.04] transition flex items-center justify-center"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://www.tiktok.com/@gementertainment.ca?lang=en"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={fr.footer.socials.tiktok}
                data-testid="footer-tiktok"
                className="w-11 h-11 rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/45 hover:bg-white/[0.04] transition flex items-center justify-center"
              >
                <TikTokIcon />
              </a>
            </div>

            <ul className="mt-8 space-y-3">
              {fr.footer.legal.map((l) => (
                <li key={l}>
                  <a
                    href="#"
                    className="text-white/55 hover:text-white text-[13.5px] transition"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-[12px] text-white/45 tracking-[0.16em] uppercase font-mono">
            {fr.footer.copyright}
          </p>
          <p className="text-[11px] text-white/30 tracking-[0.22em] uppercase font-mono">
            Conçu pour la Gen Z · Mobile-first
          </p>
        </div>

        <div className="mt-6 text-center">
          <a
            href="https://sourati.digital"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-sourati"
            className="text-[12px] text-white/40 hover:text-white tracking-[0.18em] uppercase font-mono transition"
          >
            Site réalisé par <span className="text-white/70">SOURATI DIGITAL</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
